import React from 'react';
import { useTranslation } from 'react-i18next';
import { View, ScrollView, Pressable } from 'react-native';
import { ActivityIndicator, Title } from 'react-native-paper';
import { useQuery } from 'react-query';
import { getEmergencyContacts, QueryKeys } from 'src/api';
import { Caption, Subheading } from 'src/components';
import { Margin, Size } from 'src/constants';
import { layout } from 'src/theme/globalStyles';
import styled from 'styled-components/native';
import CollapsibleSection from './CollapsibleSection';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import useGoBack from 'src/hooks/useGoBack';
import { X } from 'react-native-feather';

const DESKTOP_HEIGHT = 900;

const AfterHours: React.FC = () => {
  const { t } = useTranslation('afterHours');
  const { colors } = useAppTheme();

  const { data: emergencyContacts, isFetching } = useQuery([QueryKeys.EMERGENCY_CONTACTS], {
    queryFn: getEmergencyContacts
  });

  const goBack = useGoBack();

  if (isFetching) return <ActivityIndicator size='large' style={layout.flex1} />;

  return (
    <Container>
      <InnerView>
        <ScreenHeader>
          <BackButton onPress={() => goBack()}>
            <X width={Size.L} color={colors.onSurface} />
          </BackButton>
          <Title>{t('afterHours:title')}</Title>
        </ScreenHeader>
        {!emergencyContacts?.length && (
          <View style={[layout.flex1, layout.alignItemsCenter, layout.justifyCenter]}>
            <Subheading textAlign='center'>{t('none')}</Subheading>
          </View>
        )}
        {!!emergencyContacts?.length && (
          <CardContainer>
            <Header>{t('header')}</Header>
            {emergencyContacts?.map((practice, index) => {
              return (
                <CollapsibleSection key={practice.id} practice={practice} setOpen={index === 0} />
              );
            })}
          </CardContainer>
        )}
      </InnerView>
    </Container>
  );
};

export default AfterHours;

const Container = styled.View`
  flex: 1;
  justify-content: ${({ theme }) => (theme.viewMode.isMobile ? 'flex-end' : 'center')};
`;

const InnerView = styled.View`
  flex-shrink: 1;
  width: ${({ theme }) =>
    theme.viewMode.isMobile ? '100%' : `${theme.viewMode.maxContentWidth}px`};
  background-color: ${({ theme }) => theme.colors.surface};
  ${({ theme }) => theme.viewMode.isMobile && 'min-height: 50%;'};
  ${({ theme }) => !theme.viewMode.isMobile && 'align-self:center;'};
  ${({ theme }) => `height: ${!theme.viewMode.isMobile ? `${DESKTOP_HEIGHT}px` : '95%'}`};
  border-top-left-radius: ${({ theme }) => (!theme.viewMode.isMobile ? theme.roundness : Size.M)}px;
  border-top-right-radius: ${({ theme }) =>
    !theme.viewMode.isMobile ? theme.roundness : Size.M}px;
  border-bottom-left-radius: ${({ theme }) => (!theme.viewMode.isMobile ? theme.roundness : 0)}px;
  border-bottom-right-radius: ${({ theme }) => (!theme.viewMode.isMobile ? theme.roundness : 0)}px;
  overflow: hidden;
`;

const ScreenHeader = styled.View`
  padding: ${Size.S}px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const BackButton = styled(Pressable)`
  position: absolute;
  left: ${Margin.Medium}px;
`;

const CardContainer = styled(ScrollView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.surface};
`;

const Header = styled(Caption)`
  text-align: center;
  margin-vertical: ${Margin.Large}px;
`;
